import { AccountSecuritySchema } from '@account/schemas';
import { ChangePasswordFormValues } from '@account/types';
import { validatePassword } from '@account/utils/account';
import { Button, ButtonLink } from '@components/utility/buttons';
import { InputField } from '@components/utility/inputs';
import { useAuth } from '@hooks/useAuth';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip, Divider, Stack, Typography, useMediaQuery } from '@mui/material';
import { useUpdatePasswordMutation } from '@services/accounts';
import { handleError, toaster } from '@utils/common';
import { generateSecurePassword, isVictronAdmin } from '@utils/user';
import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const AccountChangePassword = () => {
    //#region Data Fetching
    const { user } = useAuth();
    //#endregion

    //#region Hooks
    const intl = useIntl();

    const [updatePassword, { isLoading: isUpdatingPassword }] = useUpdatePasswordMutation();

    const validationSchema = useMemo(
        () => AccountSecuritySchema(intl, user ? (isVictronAdmin(user) ? 32 : 10) : 10),
        [intl, user]
    );

    const formik = useFormik<ChangePasswordFormValues>({
        initialValues: {
            current_password: '',
            new_password: '',
            confirm_password: '',
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, helpers) => {
            try {
                await updatePassword(values).unwrap();
                helpers.resetForm();
                toaster.success({
                    title: intl.formatMessage({
                        id: 'account.security.updatePasswordSuccess',
                        defaultMessage: 'Password updated successfully',
                    }),
                    msg: intl.formatMessage({
                        id: 'account.security.updatePasswordSuccessMsg',
                        defaultMessage: 'Your password has been updated.',
                    }),
                });
            } catch (error) {
                handleError(
                    intl.formatMessage({
                        id: 'account.security.updatePasswordError',
                        defaultMessage: 'Error updating password',
                    }),
                    error
                );
            }
        },
    });

    const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down('md'));
    //#endregion

    //#region Processed data
    const passwordValidations = useMemo(
        () => validatePassword(formik.values.new_password, intl, user ? (isVictronAdmin(user) ? 32 : 10) : 10),
        [formik.values.new_password, intl, user]
    );
    //#endregion

    //#region Handlers
    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword(user ? (isVictronAdmin(user) ? 32 : 10) : 10);
        formik.setFieldValue('new_password', newPassword);
        formik.setFieldValue('confirm_password', newPassword);
    };
    //#endregion

    return (
        <FormikProvider value={formik}>
            <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                <Typography py="26px" fontFamily="museo-sans" letterSpacing="-1%" fontSize="26px">
                    <FormattedMessage id="accounts.changePassword" defaultMessage="Change your password" />
                </Typography>
                <Divider />
                <Stack mt="24px" direction="column" gap="24px">
                    <InputField.Password
                        id="current_password"
                        name="current_password"
                        buffered
                        required
                        placeholder={intl.formatMessage({
                            id: 'common.currentPassword.placeholder',
                            defaultMessage: 'Your current password',
                        })}
                        fullWidth
                        topLabel={intl.formatMessage({
                            id: 'common.currentPassword',
                            defaultMessage: 'Current Password',
                        })}
                        debounceMs={0}
                        disabled={isUpdatingPassword}
                    />
                    <Box>
                        <InputField.Password
                            id="new_password"
                            name="new_password"
                            buffered
                            placeholder={intl.formatMessage({
                                id: 'common.newPassword.placeholder',
                                defaultMessage: 'Your new password',
                            })}
                            fullWidth
                            topLabel={
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <FormattedMessage id="common.newPassword" defaultMessage="New Password" />
                                        <Typography component="span" color="signal-warning">
                                            {' '}
                                            *
                                        </Typography>
                                    </Box>{' '}
                                    <ButtonLink
                                        align="right"
                                        className="block mt-2 w-fit"
                                        fontFamily="Open Sans"
                                        fontSize={14}
                                        onClick={() => handleGeneratePassword()}
                                        tabIndex={-1}
                                        disabled={isUpdatingPassword}
                                    >
                                        <FormattedMessage
                                            id="auth.setNewPassword.generatePassword"
                                            defaultMessage="Generate Password"
                                        />
                                    </ButtonLink>
                                </Stack>
                            }
                            debounceMs={0}
                            helperText={intl.formatMessage({
                                id: 'account.security.passwordRequirements',
                                defaultMessage:
                                    "Your password can't be too similar to your other personal information.",
                            })}
                            disabled={isUpdatingPassword}
                        />

                        <Stack direction="row" spacing="12px" mt="20px" flexWrap="wrap" useFlexGap>
                            {passwordValidations.map((validation, index) => (
                                <Chip
                                    key={index}
                                    label={
                                        <Typography fontSize={14} letterSpacing={0}>
                                            {validation.label}
                                        </Typography>
                                    }
                                    color={validation.isValid ? 'light-success' : 'neutral-300'}
                                    className="black-border shadow"
                                    icon={
                                        validation.isValid ? (
                                            <CheckIcon fontSize="small" />
                                        ) : (
                                            <CloseIcon fontSize="small" />
                                        )
                                    }
                                />
                            ))}
                        </Stack>
                    </Box>
                    <InputField.Password
                        id="confirm_password"
                        name="confirm_password"
                        buffered
                        required
                        placeholder={intl.formatMessage({
                            id: 'common.confirmNewPassword.placeholder',
                            defaultMessage: 'Confirm your new password',
                        })}
                        fullWidth
                        topLabel={intl.formatMessage({
                            id: 'common.confirmNewPassword',
                            defaultMessage: 'Confirm New Password',
                        })}
                        debounceMs={0}
                        disabled={isUpdatingPassword}
                    />
                </Stack>
                <Button
                    size="large"
                    type="submit"
                    fullRounded
                    fullWidth={isBelowMd}
                    loading={isUpdatingPassword}
                    disabled={isUpdatingPassword}
                    loadingPosition="start"
                    className="mt-[24px] block ml-auto"
                >
                    <FormattedMessage id="common.updatePassword" defaultMessage="Update Password" />
                </Button>
            </form>
        </FormikProvider>
    );
};
